import { formatMoney, money, multiplyByRatio } from "@stb/core/money"
import { calculateLineGst, centsPerKmRate } from "@stb/jurisdiction-au"

import { breakEvenExpenses } from "@/trips/components/mileage/compare-methods"

/** Worked example shared by every place that prints it (copy-products.md, "Page key numbers"). */
const FY = "2026-27"
const BUSINESS_KM = 4200
const BUSINESS_PERCENT = 62
const CAR_COSTS_CENTS = 980_000

export const aud = (cents: number, whole = false) => {
  const text = formatMoney(money(cents, "AUD"), "en-AU").replace("$", "A$")
  return whole ? text.replace(/\.00$/, "") : text
}

const rate = centsPerKmRate(FY)
const centsPerKmCents = BUSINESS_KM * rate.cents
const logbookCents = multiplyByRatio(money(CAR_COSTS_CENTS, "AUD"), BUSINESS_PERCENT, 100).amount
// Same function the app uses; shown to whole dollars.
const breakEvenDollars = Math.round(
  breakEvenExpenses(FY, BUSINESS_PERCENT, BUSINESS_KM).breakEven.amount / 100,
)

export const MILEAGE_EXAMPLE = {
  financialYear: FY,
  businessKm: BUSINESS_KM.toLocaleString("en-AU"),
  businessPercent: BUSINESS_PERCENT,
  carCosts: aud(CAR_COSTS_CENTS, true),
  centsPerKm: aud(centsPerKmCents),
  logbook: aud(logbookCents),
  breakEven: aud(breakEvenDollars * 100, true),
  /** The 12.4 km trip shown in the hero card. */
  trip: {
    route: "Merrylands to Parramatta",
    km: "12.4",
    amount: aud(multiplyByRatio(money(rate.cents, "AUD"), 124, 10).amount),
  },
} as const

/** Receipt examples (copy-products.md): A$92.95 ÷ 11 = A$8.45; A$54.90 ÷ 11 ≈ A$4.99. */
const gstOf = (totalCents: number) =>
  calculateLineGst({ amount: money(totalCents, "AUD"), taxCode: "GST", amountsAre: "inclusive" })
    .gst.amount
export const RECEIPT_EXAMPLE = {
  merchant1: "Bunnings Wetherill Park",
  total1: aud(9295),
  gst1: aud(gstOf(9295)),
  merchant2: "Officeworks",
  total2: aud(5490),
  gst2: aud(gstOf(5490)),
} as const

const LINES = [
  { description: "Brake pads and labour", cents: 45_000 },
  { description: "Call-out", cents: 12_000 },
]
const subtotalCents = LINES.reduce((sum, l) => sum + l.cents, 0)
const gstCents = multiplyByRatio(money(subtotalCents, "AUD"), 1, 10).amount

/** Worked invoice example (copy-products.md): A$450.00 + A$120.00, GST A$57.00, total A$627.00. */
export const INVOICE_EXAMPLE = {
  customer: "Marrickville Motors",
  lines: LINES.map((l) => ({ description: l.description, amount: aud(l.cents) })),
  subtotal: aud(subtotalCents),
  gst: aud(gstCents),
  total: aud(subtotalCents + gstCents),
} as const
